// @ts-nocheck
// @ts-nocheck
// @ts-nocheck
/**
 * Addressbook API Client
 * Full Strapi integration with validation and transformations
 */

import { getHttpClient, i18n } from '@spcc/core';
import type {
  Address,
  AddressStats,
  AddressesFilter,
  CreateAddressRequest,
  UpdateAddressRequest,
  LocationType,
  Location,
  City,
  Region,
  Country,
  Tag,
  Contact,
  PaginatedAddresses,
  PaginationMeta,
} from '../../domain/types';
import { DEFAULT_PAGE_SIZE } from '../../domain/constants';
import {
  STRAPI_ENDPOINTS,
  type LocationListResponse,
  type LocationResponse,
  type LocationTypeListResponse,
  type CityListResponse,
  type RegionListResponse,
  type CountryListResponse,
  type TagListResponse,
  type LocationStatsResponse,
  validateLocationListResponse,
  validateLocationStatsResponse,
  validateLocationTypeDto,
} from '../../api/api-types';
import {
  transformLocationsToAddresses,
  transformLocationToAddress,
  transformLocationDtoToLocation,
  transformLocationTypeDtoToLocationType,
  transformLocationTypeDtos,
  transformCityDtoToCity,
  transformRegionDtoToRegion,
  transformCountryDtoToCountry,
  transformTagDtoToTag,
  transformContactDtoToContact,
  transformFilterToStrapiQuery,
  buildStrapiQueryString,
  transformAddressToLocationRequest,
} from '../../api/transformers';

// ============================================================================
// Default Populate Options
// ============================================================================

// Strapi 5: Max 2 levels of nesting. Keep it simple for addressbook.
// Include localizations for i18n fields so mergeLocalizations can extract the right language
const DEFAULT_LOCATION_POPULATE = {
  'populate[localizations][fields][0]': 'locale',
  'populate[localizations][fields][1]': 'name',
  'populate[localizations][fields][2]': 'addressLine1',
  'populate[localizations][fields][3]': 'addressLine2',
  'populate[city][fields][0]': 'id',
  'populate[city][fields][1]': 'documentId',
  'populate[city][fields][2]': 'code',
  'populate[city][fields][3]': 'name',
  'populate[city][fields][4]': 'stateProvince',
  'populate[city][fields][5]': 'isActive',
  'populate[city][populate][localizations][fields][0]': 'locale',
  'populate[city][populate][localizations][fields][1]': 'name',
  'populate[city][populate][localizations][fields][2]': 'stateProvince',
  'populate[city][populate][region][fields][0]': 'id',
  'populate[city][populate][region][fields][1]': 'documentId',
  'populate[city][populate][region][fields][2]': 'code',
  'populate[city][populate][region][fields][3]': 'name',
  'populate[city][populate][region][populate][localizations][fields][0]':
    'locale',
  'populate[city][populate][region][populate][localizations][fields][1]':
    'name',
  'populate[country][fields][0]': 'id',
  'populate[country][fields][1]': 'documentId',
  'populate[country][fields][2]': 'code',
  'populate[country][fields][3]': 'name',
  'populate[locationType][fields][0]': 'id',
  'populate[locationType][fields][1]': 'documentId',
  'populate[locationType][fields][2]': 'code',
  'populate[locationType][fields][3]': 'name',
  'populate[tenant][fields][0]': 'id',
  'populate[tenant][fields][1]': 'documentId',
  'populate[tenant][fields][2]': 'code',
  'populate[tenant][fields][3]': 'name',
};

const DEFAULT_CITY_POPULATE = {
  'populate[localizations][fields][0]': 'locale',
  'populate[localizations][fields][1]': 'name',
  'populate[localizations][fields][2]': 'stateProvince',
  'populate[country][fields][0]': 'id',
  'populate[country][fields][1]': 'documentId',
  'populate[country][fields][2]': 'code',
  'populate[country][fields][3]': 'name',
  'populate[region][fields][0]': 'id',
  'populate[region][fields][1]': 'documentId',
  'populate[region][fields][2]': 'code',
  'populate[region][fields][3]': 'name',
  'populate[region][populate][localizations][fields][0]': 'locale',
  'populate[region][populate][localizations][fields][1]': 'name',
};

const DEFAULT_REGION_POPULATE = {
  'populate[localizations][fields][0]': 'locale',
  'populate[localizations][fields][1]': 'name',
  'populate[country][fields][0]': 'id',
  'populate[country][fields][1]': 'documentId',
  'populate[country][fields][2]': 'code',
  'populate[country][fields][3]': 'name',
  'populate[factories][fields][0]': 'id',
  'populate[factories][fields][1]': 'documentId',
  'populate[factories][fields][2]': 'code',
  'populate[factories][fields][3]': 'name',
  'populate[factories][fields][4]': 'factoryStatus',
  'populate[factories][populate][localizations][fields][0]': 'locale',
  'populate[factories][populate][localizations][fields][1]': 'name',
};

const DEFAULT_TAG_POPULATE = {
  'populate[category][fields][0]': 'id',
  'populate[category][fields][1]': 'documentId',
  'populate[category][fields][2]': 'code',
  'populate[category][fields][3]': 'name',
};

function resolveLanguage(): string {
  return i18n.language || 'en';
}

function buildLocationArabicPayloadFromCreate(
  data: CreateAddressRequest,
): Record<string, string> {
  const out: Record<string, string> = {};
  if (data.name.ar?.trim()) out.name = data.name.ar.trim();
  if (data.addressLine1.ar?.trim())
    out.addressLine1 = data.addressLine1.ar.trim();
  if (data.addressLine2?.ar?.trim())
    out.addressLine2 = data.addressLine2.ar.trim();
  return out;
}

function buildLocationArabicPayloadFromUpdate(
  data: UpdateAddressRequest,
): Record<string, string> {
  const out: Record<string, string> = {};
  if (data.name?.ar?.trim()) out.name = data.name.ar.trim();
  if (data.addressLine1?.ar?.trim())
    out.addressLine1 = data.addressLine1.ar.trim();
  if (data.addressLine2?.ar?.trim())
    out.addressLine2 = data.addressLine2.ar.trim();
  return out;
}

function parsePositiveInt(value?: string): number | undefined {
  if (!value) return undefined;
  const normalized = value.trim();
  if (!/^\d+$/.test(normalized)) return undefined;

  const parsed = Number(normalized);
  return parsed > 0 ? parsed : undefined;
}

async function resolveLocationTypeRelationId(
  locationTypeId?: string,
  type?: Address['type'],
): Promise<number | undefined> {
  const parsedId = parsePositiveInt(locationTypeId);
  if (parsedId) return parsedId;

  // If a documentId/string was provided, map it to numeric Strapi id.
  if (locationTypeId) {
    const locationTypes = await addressbookApi.getLocationTypes();
    const byIdentifier = locationTypes.find(
      (lt) => lt.id === locationTypeId || lt.documentId === locationTypeId,
    );
    const resolvedId = parsePositiveInt(byIdentifier?.id);
    if (resolvedId) return resolvedId;
  }

  if (type) {
    const locationTypes = await addressbookApi.getLocationTypes();
    const byTypeCode = locationTypes.find(
      (lt) => lt.code.toLowerCase() === type.toLowerCase(),
    );
    const resolvedId = parsePositiveInt(byTypeCode?.id);
    if (resolvedId) return resolvedId;
  }

  return undefined;
}

// ============================================================================
// Location/Address API
// ============================================================================

export const addressbookApi = {
  /**
   * Get all addresses (locations) with optional filtering
   */
  async getAddresses(filter?: AddressesFilter): Promise<Address[]> {
    const http = getHttpClient();
    const language = resolveLanguage();
    const filterParams = transformFilterToStrapiQuery(filter ?? {});
    const queryParams = {
      ...DEFAULT_LOCATION_POPULATE,
      ...filterParams,
      locale: 'en',
    };
    const queryString = buildStrapiQueryString(queryParams);
    const url = `${STRAPI_ENDPOINTS.LOCATIONS}${queryString}`;

    const response = await http.get<LocationListResponse>(url);
    return transformLocationsToAddresses(response.data.data, language);
  },

  /**
   * Get paginated addresses with filtering
   */
  async getAddressesPaginated(
    filter?: AddressesFilter,
  ): Promise<PaginatedAddresses> {
    const http = getHttpClient();
    const language = resolveLanguage();
    const filterParams = transformFilterToStrapiQuery(filter ?? {});

    // Add pagination params
    const page = filter?.page ?? 1;
    const pageSize = filter?.pageSize ?? DEFAULT_PAGE_SIZE;
    const paginationParams = {
      'pagination[page]': String(page),
      'pagination[pageSize]': String(pageSize),
    };

    const queryParams = {
      ...DEFAULT_LOCATION_POPULATE,
      ...filterParams,
      ...paginationParams,
      locale: 'en',
    };
    const queryString = buildStrapiQueryString(queryParams);
    const url = `${STRAPI_ENDPOINTS.LOCATIONS}${queryString}`;

    const response = await http.get<LocationListResponse>(url);
    const addresses = transformLocationsToAddresses(
      response.data.data,
      language,
    );

    // Extract pagination from response
    const meta = (
      response.data as unknown as {
        meta?: { pagination?: Record<string, unknown> };
      }
    )?.meta?.pagination;

    const pagination: PaginationMeta = {
      page: (meta?.page as number) ?? page,
      pageSize: (meta?.pageSize as number) ?? pageSize,
      pageCount: (meta?.pageCount as number) ?? 1,
      total: (meta?.total as number) ?? addresses.length,
    };

    return { data: addresses, pagination };
  },

  /**
   * Get all locations (raw domain model)
   */
  async getLocations(filter?: AddressesFilter): Promise<Location[]> {
    const http = getHttpClient();
    const language = resolveLanguage();
    const filterParams = transformFilterToStrapiQuery(filter ?? {});
    const queryParams = {
      ...DEFAULT_LOCATION_POPULATE,
      ...filterParams,
      locale: 'en',
    };
    const queryString = buildStrapiQueryString(queryParams);
    const url = `${STRAPI_ENDPOINTS.LOCATIONS}${queryString}`;

    const response = await http.get<LocationListResponse>(url);
    return response.data.data.map((dto) =>
      transformLocationDtoToLocation(dto, language),
    );
  },

  /**
   * Get single address by documentId.
   * Fetches both the default (English) locale for relations and the Arabic locale
   * for localized fields, so the returned Address always has nameI18n.ar,
   * addressLine1I18n.ar, and addressLine2I18n.ar populated for the edit form.
   */
  async getAddress(documentId: string): Promise<Address | null> {
    const http = getHttpClient();
    const language = resolveLanguage();
    const queryString = buildStrapiQueryString({
      ...DEFAULT_LOCATION_POPULATE,
      locale: 'en',
    });
    const enUrl = `${STRAPI_ENDPOINTS.LOCATION_BY_ID(documentId)}${queryString}`;
    const arQueryString = buildStrapiQueryString({
      locale: 'ar',
      'fields[0]': 'name',
      'fields[1]': 'addressLine1',
      'fields[2]': 'addressLine2',
    });
    const arUrl = `${STRAPI_ENDPOINTS.LOCATION_BY_ID(documentId)}${arQueryString}`;

    try {
      const [enResponse, arResult] = await Promise.allSettled([
        http.get<LocationResponse>(enUrl),
        http.get<LocationResponse>(arUrl),
      ]);

      if (enResponse.status === 'rejected') {
        const axiosError = enResponse.reason as {
          response?: { status?: number };
        };
        if (axiosError.response?.status === 404) return null;
        throw enResponse.reason;
      }

      const address = transformLocationToAddress(
        enResponse.value.data.data,
        language,
      );

      // Merge Arabic localized fields so the edit form can pre-fill Arabic inputs
      if (arResult.status === 'fulfilled') {
        const arDto = arResult.value.data.data;
        if (arDto) {
          if (!address.nameI18n) address.nameI18n = { en: address.name };
          if (arDto.name?.trim()) {
            address.nameI18n = { ...address.nameI18n!, ar: arDto.name };
          }

          if (!address.addressLine1I18n)
            address.addressLine1I18n = { en: address.addressLine1 ?? '' };
          if (arDto.addressLine1?.trim()) {
            address.addressLine1I18n = {
              ...address.addressLine1I18n!,
              ar: arDto.addressLine1,
            };
          }

          if (arDto.addressLine2?.trim()) {
            if (!address.addressLine2I18n)
              address.addressLine2I18n = { en: address.addressLine2 ?? '' };
            address.addressLine2I18n = {
              ...address.addressLine2I18n!,
              ar: arDto.addressLine2,
            };
          }
        }
      }

      return address;
    } catch (error) {
      const axiosError = error as { response?: { status?: number } };
      if (axiosError.response?.status === 404) return null;
      throw error;
    }
  },

  /**
   * Get single location by documentId (raw domain model)
   */
  async getLocation(documentId: string): Promise<Location | null> {
    const http = getHttpClient();
    const language = resolveLanguage();
    const queryString = buildStrapiQueryString({
      ...DEFAULT_LOCATION_POPULATE,
    });
    const url = `${STRAPI_ENDPOINTS.LOCATION_BY_ID(documentId)}${queryString}`;

    try {
      const response = await http.get<LocationResponse>(url);
      return transformLocationDtoToLocation(response.data.data, language);
    } catch (error) {
      const axiosError = error as { response?: { status?: number } };
      if (axiosError.response?.status === 404) {
        return null;
      }
      throw error;
    }
  },

  /**
   * Get address statistics
   */
  async getAddressStats(): Promise<AddressStats> {
    const http = getHttpClient();
    const response = await http.get<LocationStatsResponse>(
      STRAPI_ENDPOINTS.LOCATIONS_STATS,
    );
    const validated = validateLocationStatsResponse(response.data);
    return validated.data;
  },

  /**
   * Create a new address (location)
   */
  async createAddress(
    data: CreateAddressRequest,
    tenantId?: number,
  ): Promise<Address> {
    const http = getHttpClient();

    const resolvedLocationTypeId = await resolveLocationTypeRelationId(
      data.locationTypeId,
      data.type,
    );

    const locationData = transformAddressToLocationRequest(
      data,
      tenantId,
      resolvedLocationTypeId,
    );

    // Add city and country relations if provided
    const requestData: Record<string, unknown> = { ...locationData };
    if (data.cityId) {
      requestData.city = parseInt(data.cityId, 10);
    }
    if (data.countryId) {
      requestData.country = parseInt(data.countryId, 10);
    }
    if (data.tags && data.tags.length > 0) {
      // Tags are passed as documentIds, need to resolve to numeric IDs
      // For now, pass as-is and let Strapi handle it
      requestData.tags = data.tags;
    }

    const response = await http.post<LocationResponse>(
      `${STRAPI_ENDPOINTS.LOCATIONS}?locale=en`,
      { data: requestData },
    );

    const documentId = response.data.data.documentId;
    const arPayload = buildLocationArabicPayloadFromCreate(data);
    if (Object.keys(arPayload).length > 0) {
      await http.put<LocationResponse>(
        `${STRAPI_ENDPOINTS.LOCATION_BY_ID(documentId)}?locale=ar`,
        {
          data: arPayload,
        },
      );
    }

    const created = await addressbookApi.getAddress(documentId);
    return created!;
  },

  /**
   * Update an existing address (location)
   */
  async updateAddress(
    documentId: string,
    data: UpdateAddressRequest,
  ): Promise<Address> {
    const http = getHttpClient();

    const updateData: Record<string, unknown> = {};

    // Localized fields (stored on English locale in Strapi i18n)
    if (data.name !== undefined) updateData.name = data.name.en;
    if (data.addressLine1 !== undefined)
      updateData.addressLine1 = data.addressLine1.en;
    if (data.addressLine2 !== undefined)
      updateData.addressLine2 = data.addressLine2.en;
    if (data.postalCode !== undefined) updateData.postalCode = data.postalCode;
    if (data.timezone !== undefined) updateData.timezone = data.timezone;
    if (data.status !== undefined) updateData.status = data.status;

    // Coordinates
    if (data.coordinates !== undefined) {
      if (data.coordinates === null) {
        updateData.latitude = null;
        updateData.longitude = null;
      } else {
        updateData.latitude = data.coordinates.lat;
        updateData.longitude = data.coordinates.lng;
      }
    }

    // Relations
    if (data.cityId !== undefined) {
      updateData.city = data.cityId ? parseInt(data.cityId, 10) : null;
    }
    if (data.countryId !== undefined) {
      updateData.country = data.countryId ? parseInt(data.countryId, 10) : null;
    }
    if (data.tags !== undefined) {
      updateData.tags = data.tags;
    }

    // Location type (prefer explicit locationTypeId)
    if (data.locationTypeId !== undefined) {
      if (!data.locationTypeId) {
        updateData.locationType = null;
      } else {
        const resolvedLocationTypeId = await resolveLocationTypeRelationId(
          data.locationTypeId,
          data.type,
        );
        if (resolvedLocationTypeId) {
          updateData.locationType = resolvedLocationTypeId;
        }
      }
    } else if (data.type !== undefined) {
      const resolvedLocationTypeId = await resolveLocationTypeRelationId(
        undefined,
        data.type,
      );
      if (resolvedLocationTypeId) {
        updateData.locationType = resolvedLocationTypeId;
      }
    }

    // Metadata (merge with existing or create new)
    const metadataUpdates: Record<string, unknown> = {};
    if (data.isDefault !== undefined)
      metadataUpdates.isDefault = data.isDefault;
    if (data.isFavorite !== undefined)
      metadataUpdates.isFavorite = data.isFavorite;
    if (data.contactName !== undefined)
      metadataUpdates.contactName = data.contactName;
    if (data.contactPhone !== undefined)
      metadataUpdates.contactPhone = data.contactPhone;

    if (Object.keys(metadataUpdates).length > 0) {
      // Get current metadata to merge
      const current = await addressbookApi.getAddress(documentId);
      const currentMetadata =
        (current as unknown as { metadata?: Record<string, unknown> })
          ?.metadata ?? {};
      updateData.metadata = {
        ...currentMetadata,
        ...metadataUpdates,
        ...data.metadata,
      };
    } else if (data.metadata !== undefined) {
      updateData.metadata = data.metadata;
    }

    await http.put<LocationResponse>(
      `${STRAPI_ENDPOINTS.LOCATION_BY_ID(documentId)}?locale=en`,
      { data: updateData },
    );

    const arPayload = buildLocationArabicPayloadFromUpdate(data);
    if (Object.keys(arPayload).length > 0) {
      await http.put<LocationResponse>(
        `${STRAPI_ENDPOINTS.LOCATION_BY_ID(documentId)}?locale=ar`,
        { data: arPayload },
      );
    }

    const updated = await addressbookApi.getAddress(documentId);
    return updated!;
  },

  /**
   * Delete an address (location)
   */
  async deleteAddress(documentId: string): Promise<boolean> {
    const http = getHttpClient();

    try {
      await http.delete(STRAPI_ENDPOINTS.LOCATION_BY_ID(documentId));
      return true;
    } catch {
      return false;
    }
  },

  /**
   * Toggle favorite status for an address
   */
  async toggleFavorite(documentId: string): Promise<Address> {
    const address = await addressbookApi.getAddress(documentId);
    if (!address) {
      throw new Error('Address not found');
    }

    return addressbookApi.updateAddress(documentId, {
      isFavorite: !address.isFavorite,
    });
  },

  /**
   * Set an address as the default
   */
  async setDefault(documentId: string): Promise<Address> {
    // First, unset any current default addresses
    const allAddresses = await addressbookApi.getAddresses();
    const currentDefaults = allAddresses.filter(
      (a) => a.isDefault && a.documentId !== documentId,
    );

    // Unset current defaults
    await Promise.all(
      currentDefaults.map((addr) =>
        addressbookApi.updateAddress(addr.documentId, { isDefault: false }),
      ),
    );

    // Set the new default
    return addressbookApi.updateAddress(documentId, { isDefault: true });
  },

  /**
   * Search addresses by query
   */
  async searchAddresses(query: string): Promise<Address[]> {
    return addressbookApi.getAddresses({ search: query });
  },

  /**
   * Get addresses by type
   */
  async getAddressesByType(type: Address['type']): Promise<Address[]> {
    return addressbookApi.getAddresses({ type });
  },

  /**
   * Get addresses by city
   */
  async getAddressesByCity(cityId: string): Promise<Address[]> {
    return addressbookApi.getAddresses({ cityId });
  },

  /**
   * Get all location types
   */
  async getLocationTypes(): Promise<LocationType[]> {
    const http = getHttpClient();
    const queryString = buildStrapiQueryString({
      'filters[isActive][$eq]': 'true',
      sort: 'sortOrder:asc',
    });
    const url = `${STRAPI_ENDPOINTS.LOCATION_TYPES}${queryString}`;

    const response = await http.get<LocationTypeListResponse>(url);
    return transformLocationTypeDtos(response.data.data);
  },

  /**
   * Export all locations matching current filters (no pagination limit)
   */
  async exportAll(filter?: AddressesFilter): Promise<Address[]> {
    const http = getHttpClient();
    const language = resolveLanguage();
    const params = new URLSearchParams();

    if (filter?.status && filter.status !== 'all')
      params.set('status', filter.status);
    if (filter?.search) params.set('search', filter.search);
    if (filter?.locationTypeId)
      params.set('locationTypeId', filter.locationTypeId);

    const response = await http.get<unknown>(
      `${STRAPI_ENDPOINTS.LOCATIONS}/export?${params}`,
    );
    const responseData = response.data as Record<string, unknown>;
    const raw = Array.isArray(responseData?.data) ? responseData.data : [];
    return transformLocationsToAddresses(
      raw as Parameters<typeof transformLocationsToAddresses>[0],
      language,
    );
  },
};

// ============================================================================
// Location Types API
// ============================================================================

export const locationTypesApi = {
  /**
   * Get all location types
   */
  async getAll(): Promise<LocationType[]> {
    const http = getHttpClient();
    const queryString = buildStrapiQueryString({
      'filters[isActive][$eq]': 'true',
      sort: 'sortOrder:asc',
    });
    const url = `${STRAPI_ENDPOINTS.LOCATION_TYPES}${queryString}`;

    const response = await http.get<LocationTypeListResponse>(url);
    return transformLocationTypeDtos(response.data.data);
  },

  /**
   * Get location type by documentId
   */
  async getById(documentId: string): Promise<LocationType | null> {
    const http = getHttpClient();
    const url = STRAPI_ENDPOINTS.LOCATION_TYPE_BY_ID(documentId);

    try {
      const response = await http.get<{ data: unknown }>(url);
      const validated = validateLocationTypeDto(response.data.data);
      return transformLocationTypeDtoToLocationType(validated);
    } catch (error) {
      const axiosError = error as { response?: { status?: number } };
      if (axiosError.response?.status === 404) {
        return null;
      }
      throw error;
    }
  },
};

// ============================================================================
// Cities API
// ============================================================================

export const citiesApi = {
  /**
   * Get all cities
   */
  async getAll(): Promise<City[]> {
    const http = getHttpClient();
    const language = resolveLanguage();
    const queryString = buildStrapiQueryString({
      ...DEFAULT_CITY_POPULATE,
      'filters[isActive][$eq]': 'true',
      sort: 'name:asc',
    });
    const url = `${STRAPI_ENDPOINTS.CITIES}${queryString}`;

    const response = await http.get<CityListResponse>(url);
    return response.data.data.map((dto) =>
      transformCityDtoToCity(dto, language),
    );
  },

  /**
   * Get cities by country
   */
  async getByCountry(countryIdentifier: string): Promise<City[]> {
    const http = getHttpClient();
    const language = resolveLanguage();
    const isId =
      countryIdentifier.length > 5 || /^\d+$/.test(countryIdentifier);
    const filterKey = isId
      ? 'filters[country][id][$eq]'
      : 'filters[country][code][$eq]';

    const queryString = buildStrapiQueryString({
      ...DEFAULT_CITY_POPULATE,
      'filters[isActive][$eq]': 'true',
      [filterKey]: countryIdentifier,
      sort: 'name:asc',
    });
    const url = `${STRAPI_ENDPOINTS.CITIES}${queryString}`;

    const response = await http.get<CityListResponse>(url);
    return response.data.data.map((dto) =>
      transformCityDtoToCity(dto, language),
    );
  },

  /**
   * Get city by documentId
   */
  async getById(documentId: string): Promise<City | null> {
    const http = getHttpClient();
    const language = resolveLanguage();
    const queryString = buildStrapiQueryString({ ...DEFAULT_CITY_POPULATE });
    const url = `${STRAPI_ENDPOINTS.CITY_BY_ID(documentId)}${queryString}`;

    try {
      const response = await http.get<{ data: unknown }>(url);
      return transformCityDtoToCity(
        response.data.data as Parameters<typeof transformCityDtoToCity>[0],
        language,
      );
    } catch (error) {
      const axiosError = error as { response?: { status?: number } };
      if (axiosError.response?.status === 404) {
        return null;
      }
      throw error;
    }
  },

  /**
   * Search cities by name
   */
  async search(query: string): Promise<City[]> {
    const http = getHttpClient();
    const language = resolveLanguage();
    const queryString = buildStrapiQueryString({
      ...DEFAULT_CITY_POPULATE,
      'filters[isActive][$eq]': 'true',
      'filters[name][$containsi]': query,
      sort: 'name:asc',
      'pagination[pageSize]': '20',
    });
    const url = `${STRAPI_ENDPOINTS.CITIES}${queryString}`;

    const response = await http.get<CityListResponse>(url);
    return response.data.data.map((dto) =>
      transformCityDtoToCity(dto, language),
    );
  },
};

// ============================================================================
// Regions API
// ============================================================================

export const regionsApi = {
  /**
   * Get all regions with factories populated
   */
  async getAll(): Promise<Region[]> {
    const http = getHttpClient();
    const language = resolveLanguage();
    const queryString = buildStrapiQueryString({
      ...DEFAULT_REGION_POPULATE,
      'filters[isActive][$eq]': 'true',
      sort: 'name:asc',
    });
    const url = `${STRAPI_ENDPOINTS.REGIONS}${queryString}`;

    const response = await http.get<RegionListResponse>(url);
    return response.data.data.map((dto) =>
      transformRegionDtoToRegion(dto, language),
    );
  },

  /**
   * Get region by documentId with factories populated
   */
  async getById(documentId: string): Promise<Region | null> {
    const http = getHttpClient();
    const language = resolveLanguage();
    const queryString = buildStrapiQueryString({
      ...DEFAULT_REGION_POPULATE,
    });
    const url = `${STRAPI_ENDPOINTS.REGION_BY_ID(documentId)}${queryString}`;

    try {
      const response = await http.get<{ data: unknown }>(url);
      return transformRegionDtoToRegion(
        response.data.data as Parameters<typeof transformRegionDtoToRegion>[0],
        language,
      );
    } catch (error) {
      const axiosError = error as { response?: { status?: number } };
      if (axiosError.response?.status === 404) {
        return null;
      }
      throw error;
    }
  },
};

// ============================================================================
// Countries API
// ============================================================================

export const countriesApi = {
  /**
   * Get all countries
   */
  async getAll(): Promise<Country[]> {
    const http = getHttpClient();
    const queryString = buildStrapiQueryString({
      'filters[isActive][$eq]': 'true',
      sort: 'name:asc',
    });
    const url = `${STRAPI_ENDPOINTS.COUNTRIES}${queryString}`;

    const response = await http.get<CountryListResponse>(url);
    return response.data.data.map(transformCountryDtoToCountry);
  },

  /**
   * Get country by code (3-letter ISO)
   */
  async getByCode(code: string): Promise<Country | null> {
    const http = getHttpClient();
    const queryString = buildStrapiQueryString({
      'filters[code][$eq]': code.toUpperCase(),
    });
    const url = `${STRAPI_ENDPOINTS.COUNTRIES}${queryString}`;

    const response = await http.get<CountryListResponse>(url);
    if (response.data.data.length === 0) {
      return null;
    }
    return transformCountryDtoToCountry(response.data.data[0]);
  },

  /**
   * Get country by documentId
   */
  async getById(documentId: string): Promise<Country | null> {
    const http = getHttpClient();
    const url = STRAPI_ENDPOINTS.COUNTRY_BY_ID(documentId);

    try {
      const response = await http.get<{ data: unknown }>(url);
      return transformCountryDtoToCountry(
        response.data.data as Parameters<
          typeof transformCountryDtoToCountry
        >[0],
      );
    } catch (error) {
      const axiosError = error as { response?: { status?: number } };
      if (axiosError.response?.status === 404) {
        return null;
      }
      throw error;
    }
  },
};

// ============================================================================
// Tags API
// ============================================================================

export const tagsApi = {
  /**
   * Get all tags
   */
  async getAll(): Promise<Tag[]> {
    const http = getHttpClient();
    const queryString = buildStrapiQueryString({
      ...DEFAULT_TAG_POPULATE,
      'filters[isActive][$eq]': 'true',
      sort: 'sortOrder:asc,name:asc',
    });
    const url = `${STRAPI_ENDPOINTS.TAGS}${queryString}`;

    const response = await http.get<TagListResponse>(url);
    return response.data.data.map(transformTagDtoToTag);
  },

  /**
   * Get tags by category code
   */
  async getByCategory(categoryCode: string): Promise<Tag[]> {
    const http = getHttpClient();
    const queryString = buildStrapiQueryString({
      ...DEFAULT_TAG_POPULATE,
      'filters[isActive][$eq]': 'true',
      'filters[category][code][$eq]': categoryCode,
      sort: 'sortOrder:asc,name:asc',
    });
    const url = `${STRAPI_ENDPOINTS.TAGS}${queryString}`;

    const response = await http.get<TagListResponse>(url);
    return response.data.data.map(transformTagDtoToTag);
  },

  /**
   * Get tag by documentId
   */
  async getById(documentId: string): Promise<Tag | null> {
    const http = getHttpClient();
    const queryString = buildStrapiQueryString(DEFAULT_TAG_POPULATE);
    const url = `${STRAPI_ENDPOINTS.TAG_BY_ID(documentId)}${queryString}`;

    try {
      const response = await http.get<{ data: unknown }>(url);
      return transformTagDtoToTag(
        response.data.data as Parameters<typeof transformTagDtoToTag>[0],
      );
    } catch (error) {
      const axiosError = error as { response?: { status?: number } };
      if (axiosError.response?.status === 404) {
        return null;
      }
      throw error;
    }
  },
};

// ============================================================================
// Contacts API
// ============================================================================

export const contactsApi = {
  /**
   * Get contacts for a location
   */
  async getByLocation(locationDocumentId: string): Promise<Contact[]> {
    const http = getHttpClient();
    const queryString = buildStrapiQueryString({
      'filters[location][documentId][$eq]': locationDocumentId,
      'filters[isActive][$eq]': 'true',
      sort: 'isPrimary:desc,name:asc',
    });
    const url = `${STRAPI_ENDPOINTS.CONTACTS}${queryString}`;

    const response = await http.get<{ data: unknown[] }>(url);
    return (
      response.data.data as Parameters<typeof transformContactDtoToContact>[0][]
    ).map(transformContactDtoToContact);
  },

  /**
   * Get contact by documentId
   */
  async getById(documentId: string): Promise<Contact | null> {
    const http = getHttpClient();
    const url = STRAPI_ENDPOINTS.CONTACT_BY_ID(documentId);

    try {
      const response = await http.get<{ data: unknown }>(url);
      return transformContactDtoToContact(
        response.data.data as Parameters<
          typeof transformContactDtoToContact
        >[0],
      );
    } catch (error) {
      const axiosError = error as { response?: { status?: number } };
      if (axiosError.response?.status === 404) {
        return null;
      }
      throw error;
    }
  },
};
